import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { climbVideosService } from '../../services/climb-videos';
import { useAuth } from '../../hooks/useAuth';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { toggleLikeLocal } from '../../utils/climb-video-like';
import type { ClimbVideo } from '../../types/climb-video';
import { ClimbVideoViewer } from './ClimbVideoViewer';
import { UploadClimbVideoDialog } from './UploadClimbVideoDialog';
import { shareDialogPath, type ShareNavigationState } from './share/share-navigation';

interface ClimbVideoCarouselProps {
  climbSlug: string;
  climbName: string;
  /** From climb.videos_count — avoids fetching for climbs with no videos. */
  videosCount: number;
  /** Base climb URL (no trailing /video/:id); the open video lives at `${climbPath}/video/:id`. */
  climbPath: string;
  /** Increment to open the upload dialog (the climb page's FAB; logged-in users only). */
  addVideoTrigger?: number;
}

/**
 * Horizontally-scrolling strip of small square video previews (uploader avatar
 * bottom-right). Tapping a preview opens the fullscreen viewer; the open video
 * is a real route (`${climbPath}/video/:id`) so it's directly linkable. With no
 * videos the strip is not drawn at all; adding one starts from the climb page's FAB.
 */
export function ClimbVideoCarousel({ climbSlug, climbName, videosCount, climbPath, addVideoTrigger }: ClimbVideoCarouselProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { videoId } = useParams<{ videoId?: string }>();
  // When the viewer is opened from the feed (ClimbMasonryGrid passes startTime in the
  // navigation state), resume the video where the feed preview left off instead of
  // restarting it. Only the initial open carries this; prev/next replace it without state.
  const startTime = (location.state as { startTime?: number } | null)?.startTime;
  const [videos, setVideos] = useState<ClimbVideo[]>([]);
  const [muted, setMuted] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Fetch the list when the climb actually has videos (a valid deep link implies it does).
  useEffect(() => {
    if (videosCount <= 0) { setVideos([]); return; }
    let active = true;
    climbVideosService.getClimbVideos(climbSlug)
      .then(data => { if (active) setVideos(data); })
      .catch(() => { /* leave empty */ });
    return () => { active = false; };
  }, [climbSlug, videosCount]);

  // External trigger: open the upload dialog (from the FAB).
  useEffect(() => {
    if (addVideoTrigger && user) setUploadOpen(true);
  }, [addVideoTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const openIndex = videoId ? videos.findIndex(v => v.id === videoId) : -1;
  const openVideo = openIndex >= 0 ? videos[openIndex] : null;
  const openVideoId = openVideo?.id ?? null;

  // Register a view whenever a video is opened (including prev/next).
  useEffect(() => {
    if (!openVideoId) return;
    climbVideosService.registerView(climbSlug, openVideoId).catch(() => {});
  }, [openVideoId, climbSlug]);

  const goToVideo = (id: string) => navigate(`${climbPath}/video/${id}`);
  const replaceVideo = (id: string) => navigate(`${climbPath}/video/${id}`, { replace: true });
  const closeViewer = () => navigate(climbPath, { replace: true });

  const handleDelete = async (id: string) => {
    await climbVideosService.deleteClimbVideo(climbSlug, id);
    setVideos(prev => prev.filter(v => v.id !== id));
    closeViewer();
  };

  const replaceInList = (updated: ClimbVideo) =>
    setVideos(prev => prev.map(v => (v.id === updated.id ? updated : v)));

  const handleToggleLike = () => {
    if (!user || !openVideoId) return;
    const id = openVideoId;
    // Optimistic toggle; reconcile with (or revert to) the server response.
    setVideos(prev => prev.map(v => (v.id === id ? toggleLikeLocal(v, user) : v)));
    climbVideosService.toggleLike(climbSlug, id)
      .then(replaceInList)
      .catch(() => setVideos(prev => prev.map(v => (v.id === id ? toggleLikeLocal(v, user) : v))));
  };

  const handleRegisterShare = () => {
    // A share counts once per user: skip if already shared (the sheet still opened).
    if (!user || !openVideo || openVideo.shared_by_me) return;
    const id = openVideo.id;
    setVideos(prev => prev.map(v => (v.id === id ? { ...v, shares_count: v.shares_count + 1, shared_by_me: true } : v)));
    climbVideosService.registerShare(climbSlug, id)
      .then(replaceInList)
      .catch(() => setVideos(prev => prev.map(v => (v.id === id ? { ...v, shares_count: Math.max(0, v.shares_count - 1), shared_by_me: false } : v))));
  };

  const likedByMe = !!(user && openVideo && openVideo.likers.some(l => l.id === String(user.id)));

  return (
    // Break fully out of the page's horizontal padding, then re-inset less, so the
    // strip sits closer to the screen edge than the rest of the content. The
    // spacing lives on the strip, so a climb with no videos takes no room at all.
    <div className="-mx-4 md:-mx-6">
      {/* The thumbnail strip is only for the climb page; on /video/:id the viewer replaces it. */}
      {!videoId && videos.length > 0 && (
        <div data-swipe-ignore className="flex gap-2 overflow-x-auto px-2 pt-2 pb-2 md:px-3">
          {videos.map(video => (
            <button
              key={video.id}
              type="button"
              data-testid="video-thumb"
              aria-label={t('climbVideos.openVideo')}
              onClick={() => goToVideo(video.id)}
              className="relative aspect-square w-20 shrink-0 overflow-hidden rounded-lg bg-black ring-1 ring-border transition-transform hover:scale-[1.02]"
            >
              {video.video_url && (
                <video
                  src={`${video.video_url}#t=0.1`}
                  poster={video.poster_url ?? undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="pointer-events-none h-full w-full object-cover"
                />
              )}
              <span className="absolute bottom-0.5 right-0.5">
                <Avatar className="h-5 w-5 ring-2 ring-white">
                  <AvatarImage src={video.user.avatar_small_url ?? undefined} alt={video.user.username} />
                  <AvatarFallback className="text-[9px]">{video.user.username.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
              </span>
            </button>
          ))}
        </div>
      )}

      {user && (
        <UploadClimbVideoDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          climbSlug={climbSlug}
          onUploaded={(video) => setVideos(prev => [video, ...prev])}
        />
      )}

      {openVideo && (
        <ClimbVideoViewer
          video={openVideo}
          muted={muted}
          isOwner={user != null && String(user.id) === openVideo.user.id}
          canShare={user != null}
          canPrev={openIndex > 0}
          canNext={openIndex >= 0 && openIndex < videos.length - 1}
          onPrev={() => { if (openIndex > 0) replaceVideo(videos[openIndex - 1].id); }}
          onNext={() => { if (openIndex >= 0 && openIndex < videos.length - 1) replaceVideo(videos[openIndex + 1].id); }}
          onToggleMute={() => setMuted(m => !m)}
          onClose={closeViewer}
          onDelete={handleDelete}
          onEdit={() => navigate(shareDialogPath(climbPath), { state: { presetVideoUrl: openVideo.video_url ?? undefined, openedInApp: true } satisfies ShareNavigationState })}
          shareTitle={climbName}
          initialTime={startTime}
          likedByMe={likedByMe}
          sharesCount={openVideo.shares_count}
          onToggleLike={handleToggleLike}
          onRegisterShare={handleRegisterShare}
        />
      )}
    </div>
  );
}
